import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { invoiceFinancialBillableItem, invoiceFinancialPackageInstallment, readFinancialPackageInstallments } from '../../services/FinancialReadService';

type ActionName = 'read' | 'installment' | 'billable';

const initialForm = {
  packageId: '',
  installmentId: '',
  billableItemId: '',
  dueDate: '',
};

const actionLabels: Record<ActionName, string> = {
  read: 'Consultar parcelas',
  installment: 'Faturar parcela',
  billable: 'Faturar item faturável',
};

export default function FinancialA3RpcTestPage() {
  const { selectedClient } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [result, setResult] = useState<unknown>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState<ActionName | null>(null);
  const [history, setHistory] = useState<string[]>([]);

  useEffect(() => {
    setForm(initialForm);
    setResult(null);
    setError('');
    setHistory([]);
  }, [selectedClient?.clientId]);

  const update = (key: keyof typeof initialForm, value: string) =>
    setForm(current => ({ ...current, [key]: value }));

  async function run(action: ActionName) {
    if (!selectedClient?.clientId) {
      setError('Selecione um tenant antes de executar a operação.');
      return;
    }
    setLoading(action);
    setError('');
    setResult(null);
    try {
      let data: unknown;
      if (action === 'read') {
        if (!form.packageId.trim()) throw new Error('Informe o identificador do pacote para consultar as parcelas.');
        data = await readFinancialPackageInstallments(selectedClient.clientId, form.packageId.trim());
      } else if (action === 'installment') {
        if (!form.installmentId.trim()) throw new Error('Informe o identificador da parcela.');
        data = await invoiceFinancialPackageInstallment(selectedClient.clientId, form.installmentId.trim());
      } else {
        if (!form.billableItemId.trim()) throw new Error('Informe o identificador do item faturável.');
        data = await invoiceFinancialBillableItem(selectedClient.clientId, form.billableItemId.trim(), form.dueDate);
      }
      setResult(data);
      setHistory(current => [`${new Date().toLocaleString('pt-BR')} — ${actionLabels[action]}: sucesso`, ...current]);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'A operação falhou sem mensagem detalhada.';
      setError(message);
      setHistory(current => [`${new Date().toLocaleString('pt-BR')} — ${actionLabels[action]}: falha observada`, ...current]);
    } finally {
      setLoading(null);
    }
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void run('read');
  };

  return (
    <main className="min-h-screen bg-slate-100 px-4 py-8 sm:px-8">
      <section className="mx-auto max-w-5xl rounded-lg bg-white p-6 shadow-sm">
        <div className="mb-6 flex items-start justify-between gap-4 border-b pb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Acesso temporário por URL</p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">Teste das RPCs A3</h1>
            <p className="mt-1 text-sm text-slate-600">Leitura de parcelas e faturamento seguro de item ou parcela.</p>
          </div>
          <Link className="text-sm text-blue-700 hover:underline" to="../admin/financial-invoices">Voltar</Link>
        </div>

        <div className="mb-5 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          Tenant selecionado: <strong>{selectedClient?.clientId ?? 'nenhum'}</strong>. As operações usam o tenant selecionado e exigem vínculo administrativo.
          <br />Use identificadores reais ou inexistentes para registrar os cenários negativos pendentes. Faturar um item/parcela pode alterar o estado financeiro.
        </div>

        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <label>
            <span className="mb-1 block text-sm font-medium text-slate-700">ID do pacote</span>
            <input value={form.packageId} onChange={event => update('packageId', event.target.value)} className="w-full rounded border border-slate-300 px-3 py-2 text-sm" placeholder="Para consultar parcelas" />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-slate-700">ID da parcela</span>
            <input value={form.installmentId} onChange={event => update('installmentId', event.target.value)} className="w-full rounded border border-slate-300 px-3 py-2 text-sm" placeholder="packageBillingInstallmentId" />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-slate-700">ID do item faturável</span>
            <input value={form.billableItemId} onChange={event => update('billableItemId', event.target.value)} className="w-full rounded border border-slate-300 px-3 py-2 text-sm" placeholder="billableItemId" />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-slate-700">Vencimento opcional</span>
            <input type="date" value={form.dueDate} onChange={event => update('dueDate', event.target.value)} className="w-full rounded border border-slate-300 px-3 py-2 text-sm" />
          </label>

          <div className="flex flex-wrap gap-3 border-t pt-4 sm:col-span-2">
            <button type="submit" disabled={loading !== null || !selectedClient} className="rounded bg-blue-700 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
              {loading === 'read' ? 'Consultando…' : actionLabels.read}
            </button>
            <button type="button" disabled={loading !== null || !selectedClient} onClick={() => void run('installment')} className="rounded bg-indigo-700 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
              {loading === 'installment' ? 'Faturando…' : actionLabels.installment}
            </button>
            <button type="button" disabled={loading !== null || !selectedClient} onClick={() => void run('billable')} className="rounded bg-indigo-700 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
              {loading === 'billable' ? 'Faturando…' : actionLabels.billable}
            </button>
          </div>
        </form>

        {error && <pre role="alert" className="mt-5 whitespace-pre-wrap rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</pre>}
        {result !== null && <pre className="mt-5 overflow-auto rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">{JSON.stringify(result, null, 2)}</pre>}
        {history.length > 0 && <section className="mt-5 rounded border border-slate-200 bg-slate-50 p-3"><h2 className="text-sm font-semibold text-slate-800">Histórico desta sessão</h2><ul className="mt-2 space-y-1 text-xs text-slate-600">{history.map((entry, index) => <li key={`${entry}-${index}`}>{entry}</li>)}</ul></section>}
      </section>
    </main>
  );
}
